<template>
    <!-- Renderless component that manages session timeout notification -->
    <div style="display: none;" />
</template>

<script>
import { mapActions, mapState } from 'vuex';

export default {
    name: 'SessionTimeoutNotification',

    data() {
        return {
            isRedirecting: false,
            snackbarInstance: null,
        };
    },

    computed: {
        ...mapState('Auth', ['sessionTimedOut']),

        isSessionTimedOut() {
            return this.sessionTimedOut;
        },
    },

    watch: {
        // Watch for session timeout state changes
        isSessionTimedOut(newValue) {
            if (newValue === true && !this.isRedirecting) {
                this.showNotification();
                this.checkRedirectStatus();
            } else {
                this.closeNotification();
            }
        },
    },

    mounted() {
        // Check if redirect is already in progress when component mounts
        this.checkRedirectStatus();

        // Listen for storage events to coordinate across tabs
        window.addEventListener('storage', this.handleStorageEvent);

        // Listen for the custom session-timeout event
        window.addEventListener('session-timeout', this.handleSessionTimeoutEvent);

        // Check initial state
        if (this.isSessionTimedOut && !this.isRedirecting) {
            this.showNotification();
        }
    },

    beforeUnmount() {
        // Clean up event listeners
        window.removeEventListener('storage', this.handleStorageEvent);
        window.removeEventListener('session-timeout', this.handleSessionTimeoutEvent);
        this.closeNotification();
    },

    methods: {
        ...mapActions('Auth', ['setSessionTimedOut']),

        showNotification() {
            // SessionMonitor owns expiry UX (redirect + login notice) — skip duplicate toasts
            if (this.snackbarInstance || window.SessionMonitor?.isSessionTimedOut()) {
                return;
            }

            this.snackbarInstance = this.$snackbar.open({
                message: 'Your session has expired. Please log in again to continue. Any unsaved work will be lost.',
                type: 'is-warning',
                position: 'is-bottom-left',
                indefinite: true,
                actionText: 'Log in now',
                onAction: () => this.redirectToLogin(),
            });
        },

        closeNotification() {
            if (this.snackbarInstance) {
                this.snackbarInstance.close();
                this.snackbarInstance = null;
            }
        },

        /**
         * Check if a redirect is already in progress
         */
        checkRedirectStatus() {
            // Check SessionMonitor state
            if (window.SessionMonitor && window.SessionMonitor.isRedirectInProgress()) {
                this.isRedirecting = true;
                this.closeNotification();
            }
        },

        /**
         * Handle storage events for cross-tab communication
         */
        handleStorageEvent(event) {
            if (event.key === 'sessionMonitorState') {
                try {
                    const newState = JSON.parse(event.newValue || '{}');
                    if (newState.redirectInProgress) {
                        this.isRedirecting = true;
                        this.closeNotification();
                    }
                } catch (error) {
                    // Invalid JSON in localStorage
                }
            }
        },

        /**
         * Handle the custom session-timeout event
         */
        handleSessionTimeoutEvent() {
            if (!this.isRedirecting) {
                this.showNotification();
            }
        },

        /**
         * Redirect to login page
         */
        redirectToLogin() {
            this.isRedirecting = true;
            this.closeNotification();

            // Mark that we're redirecting to prevent other components from redirecting too
            if (window.SessionMonitor) {
                window.SessionMonitor.markRedirectInProgress();
            }

            // Get current full path (including query params and hash) and build redirect URL
            const currentPath = window.location.pathname + window.location.search + window.location.hash;
            let loginUrl = '/login';

            const pathOnly = window.location.pathname;
            if (pathOnly !== '/login' && pathOnly !== '/') {
                const encodedPath = encodeURIComponent(currentPath);
                loginUrl = `/login?redirect=${encodedPath}`;
            }

            // Redirect to login page with redirect parameter
            try {
                window.location.replace(loginUrl);
            } catch (error) {
                window.location.href = loginUrl;
            }
        },
    },
};
</script>
